import axios, { AxiosResponse } from 'axios';
import { User, LoginCredentials, ApiResponse, UserApiResponse, UpdatePreferencesRequest, UpdatePreferencesResponse } from '../types';

const API_URL = process.env.REACT_APP_API_URL as string;

// Function to handle login
export const login = async (username: string, password: string): Promise<User> => {
  try {
    const credentials: LoginCredentials = {
      user: username,
      password: password
    };
    
    const response: AxiosResponse<User> = await axios.post(`${API_URL}/api/login`, credentials);
    
    if (response.data.access_token) {
      // Store user details and token in localStorage
      localStorage.setItem('user', JSON.stringify(response.data));
    }
    
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Function to logout
export const logout = (): void => {
  localStorage.removeItem('user');
};

// Function to get current user
export const getCurrentUser = (): User | null => {
  const userStr = localStorage.getItem('user');
  if (userStr) {
    try {
      return JSON.parse(userStr) as User;
    } catch (error) {
      console.error('Error parsing user data:', error);
      return null;
    }
  }
  return null;
};

export const getStoredUserRole = (): string | null => {
  const user = getCurrentUser();
  return user?.user?.role ?? null;
};

export const isAdminFinca = (): boolean => {
  return getStoredUserRole() === 'admin_finca';
};

/** ID de finca asignada guardado en el objeto user del login (si el backend lo envía). */
export const getAssignedFincaId = (): string | null => {
  const user = getCurrentUser();
  const id = user?.user?.finca_id;
  if (typeof id === 'string' && id.trim()) return id.trim();
  return null;
};

// Function to check if user is authenticated
export const isAuthenticated = (): boolean => {
  const user = getCurrentUser();
  return !!user && !!user.access_token;
};

// Function to get auth header
export const getAuthHeader = (): { Authorization?: string } => {
  const user = getCurrentUser();
  if (user && user.access_token) {
    return { Authorization: `Bearer ${user.access_token}` };
  } else {
    return {};
  }
};

// Function to get user data from API
export const getUserData = async (): Promise<UserApiResponse> => {
  try {
    const user = getCurrentUser();
    if (!user || !user.access_token) {
      throw new Error('No authentication token available');
    }

    const response: AxiosResponse<UserApiResponse> = await axios.get(
      `${API_URL}/api/user`,
      {
        headers: {
          'Authorization': `Bearer ${user.access_token}`
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error fetching user data:', error);
    throw error;
  }
};

// Function to update user message preferences
export const updateUserPreferences = async (preferences: UpdatePreferencesRequest): Promise<UpdatePreferencesResponse> => {
  try {
    const user = getCurrentUser();
    if (!user || !user.access_token) {
      throw new Error('No authentication token available');
    }

    const response: AxiosResponse<UpdatePreferencesResponse> = await axios.put(
      `${API_URL}/api/user/messages-pref`,
      preferences,
      {
        headers: {
          'Authorization': `Bearer ${user.access_token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error updating user preferences:', error);
    throw error;
  }
};

